import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, MeStats } from "../lib/api";
import { useAuth } from "../lib/auth-context";

export default function Dashboard() {
  const { me } = useAuth();
  const [stats, setStats] = useState<MeStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!me?.signed_in) return;
    api
      .meStats()
      .then(setStats)
      .catch(() => setError("Could not load your stats."));
  }, [me?.signed_in]);

  if (!me) return null;
  if (!me.signed_in) return <Navigate to="/login" replace />;
  if (!me.access_code_verified) return <Navigate to="/access-code" replace />;

  if (error) {
    return (
      <div className="page">
        <p className="muted" style={{ color: "var(--negative)" }}>{error}</p>
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="page">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const hasActivity = stats.attempts > 0;

  return (
    <div className="page page--wide">
      <div className="stack-lg">
        <header>
          <h1 className="h1" style={{ marginBottom: 4 }}>Your practice</h1>
          <p className="lede" style={{ margin: 0 }}>
            Signed in as <strong>{stats.blazerid}</strong>
            {stats.last_active && (
              <>
                {" "}· last active {fmtRelative(stats.last_active)}
              </>
            )}
          </p>
        </header>

        {!hasActivity && (
          <div className="card stack">
            <strong style={{ fontSize: 18 }}>Nothing here yet.</strong>
            <p className="muted" style={{ margin: 0 }}>
              Once you've answered a few questions, you'll see your stats and
              the concepts you've worked through here.
            </p>
            <div>
              <Link className="btn" to="/practice">Start practicing</Link>
            </div>
          </div>
        )}

        {hasActivity && (
          <>
            <div className="stat-grid">
              <Stat label="Questions answered" value={String(stats.attempts)} />
              <Stat label="Correct" value={String(stats.correct)} />
              <Stat label="Accuracy" value={fmtPct(stats.pct_correct)} />
              <Stat label="Avg time / Q" value={fmtTime(stats.avg_time_ms)} />
              <Stat label="Sessions" value={String(stats.sessions)} />
              <Stat
                label="First active"
                value={stats.first_active ? fmtDate(stats.first_active) : "-"}
              />
            </div>

            <div className="card stack" style={{ padding: 0 }}>
              <div className="card__section-head">Concepts you've practiced</div>
              <div className="admin__table-wrap">
                <table className="admin__table">
                  <thead>
                    <tr>
                      <th>Concept</th>
                      <th>Attempts</th>
                      <th>Correct</th>
                      <th>Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_concept.map((c) => (
                      <tr key={c.concept_name}>
                        <td>
                          <div className="admin__crumb">{c.topic_name ?? ""}</div>
                          <div>{c.concept_name}</div>
                        </td>
                        <td className="num">{c.attempts}</td>
                        <td className="num">{c.correct}</td>
                        <td className="num">{fmtPct(c.pct_correct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card stack" style={{ padding: 0 }}>
              <div className="card__section-head">Recent</div>
              <ul className="dash-recent">
                {stats.recent.map((r, i) => (
                  <li key={`${r.at}-${i}`} className="dash-recent__row">
                    <div className={`dash-recent__dot dash-recent__dot--${r.correct ? "ok" : "miss"}`} />
                    <div className="dash-recent__body">
                      <div className="dash-recent__concept">{r.concept_name}</div>
                      <div className="dash-recent__meta">
                        {r.correct ? "Correct" : "Incorrect"} · {fmtTime(r.time_ms)} · {fmtRelative(r.at)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
    </div>
  );
}

function fmtPct(v: number | null): string {
  if (v === null || v === undefined) return "-";
  return `${Math.round(v * 100)}%`;
}
function fmtTime(ms: number | null): string {
  if (ms === null || ms === undefined) return "-";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}
function fmtRelative(iso: string | null): string {
  if (!iso) return "-";
  const t = new Date(iso.replace(" ", "T") + "Z").getTime();
  if (Number.isNaN(t)) return iso;
  const dMs = Date.now() - t;
  const min = Math.round(dMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}
function fmtDate(iso: string): string {
  const t = new Date(iso.replace(" ", "T") + "Z");
  if (Number.isNaN(t.getTime())) return iso;
  return t.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
